import React, { useState } from 'react';
import ImportCodeButton from '../../../components/ImportCodeButton';
import { Episode, Project } from '../../../types';
import { ScrollingProjectName } from '../../../components/ScrollingProjectName';
import {
  getScriptFileValidationMessageKey,
  SCRIPT_UPLOAD_ACCEPT,
  validateScriptUploadFile,
} from '../../../utils/scriptFileValidation';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface AppendedScriptFile {
  fileName: string;
  fileURL: string;
  fileSize: number;
  episodes: string;
  createdAt: string;
}

interface ProjectScriptPanelProps {
  project: Project;
  episodes: Episode[];
  polling: boolean;
  selectedFile: File | null;
  uploading: boolean;
  appendFile: File | null;
  appending: boolean;
  showAppendForm: boolean;
  scriptMenuOpen: boolean;
  scriptMenuRef: React.RefObject<HTMLDivElement>;
  analysisStatus?: string;
  t: Translate;
  onAppendFileChange: (file: File | null) => void;
  onShowAppendForm: () => void;
  onCancelAppend: () => void;
  onAppend: () => void;
  onUpload: (event: React.FormEvent) => void;
  onOpenFormatGuide: () => void;
  onRetryScript: () => void;
  onReupload: () => void;
  onScriptMenuOpenChange: (open: boolean | ((previous: boolean) => boolean)) => void;
  onImported: () => void;
  onInvalidFile: (messageKey?: string) => void;
}

function parseAppendedFiles(project: Project): AppendedScriptFile[] {
  try {
    if (!project.script?.appendedFiles) return [];
    const parsed = JSON.parse(project.script.appendedFiles);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function ProjectScriptPanel({
  project,
  episodes,
  selectedFile,
  uploading,
  appendFile,
  appending,
  showAppendForm,
  scriptMenuOpen,
  scriptMenuRef,
  t,
  onAppendFileChange,
  onShowAppendForm,
  onCancelAppend,
  onAppend,
  onUpload,
  onOpenFormatGuide,
  onRetryScript,
  onReupload,
  onScriptMenuOpenChange,
  onImported,
  onInvalidFile,
}: ProjectScriptPanelProps) {
  const [expanded, setExpanded] = useState(false);

  if (project.script) {
    const appended = parseAppendedFiles(project);
    const hasMultiple = appended.length > 0;
    const canAppend = project.script.status === 'completed' && episodes.length > 0 && project.analysisMode !== 'external';

    const boxStyle: React.CSSProperties = {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
      width: '100%', height: '32px', borderRadius: '6px', fontSize: '0.8rem',
      background: 'rgba(99,102,241,0.08)', color: 'var(--theme-project)',
      border: '1px solid rgba(99,102,241,0.25)', boxSizing: 'border-box',
    };
    const renderFileBox = (fileName: string, fileURL: string | undefined, episodesLabel: string | undefined, key: React.Key) => (
      fileURL ? (
        <a key={key} href={fileURL} target="_blank" rel="noopener noreferrer" title={fileName} style={{ ...boxStyle, textDecoration: 'none' }}>
          <ScrollingProjectName name={fileName} className="script-file-name" />
          {episodesLabel && <span style={{ fontWeight: 500, flexShrink: 0 }}>{episodesLabel}</span>}
        </a>
      ) : (
        <div key={key} title={fileName} style={boxStyle}>
          <ScrollingProjectName name={fileName} className="script-file-name" />
          {episodesLabel && <span style={{ fontWeight: 500, flexShrink: 0 }}>{episodesLabel}</span>}
        </div>
      )
    );

    return (
      <div>
        {/* 标题行：剧本标题 + 已上传剧本框（多剧本时框右侧下拉箭头）+ 小号追加按钮 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', flexShrink: 0 }}>{t('script.title')}</h3>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {renderFileBox(project.script.fileName, project.script.fileURL, undefined, 'main')}
            </div>
            {hasMultiple && (
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                aria-expanded={expanded}
                title={t('script.title')}
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '6px', border: '1px solid var(--border)', background: expanded ? 'var(--surface-2)' : 'transparent', color: 'var(--text-2)', cursor: 'pointer', padding: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            )}
          </div>
          {canAppend && !showAppendForm && (
            <button
              type="button"
              onClick={onShowAppendForm}
              title={t('script.appendEpisode')}
              style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '0.25rem', height: '24px', padding: '0 8px', fontSize: '0.72rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              {t('script.appendEpisode')}
            </button>
          )}
        </div>

        {/* 多剧本展开：列出全部已上传剧本（默认不堆叠，按需展开） */}
        {hasMultiple && expanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.35rem' }}>
            {appended.map((file, index) => renderFileBox(file.fileName, file.fileURL, file.episodes, index))}
          </div>
        )}
        {project.script.status === 'failed' && project.script.errorMsg && (
          <>
            <p className="error-text" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>&#10060; {t('script.error', { msg: project.script.errorMsg })}</p>
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={onRetryScript}>
                {t('script.retryProcess')}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={onReupload}>
                {t('script.reUpload')}
              </button>
            </div>
          </>
        )}
        {project.script.status === 'completed' && episodes.length > 0 && showAppendForm && project.analysisMode !== 'external' && (
          <div style={{ marginTop: '0.5rem' }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: 'var(--text-2)' }}>
              {t('script.appendHint', { count: episodes.length, max: String(Math.max(...episodes.map(e => e.index))).padStart(2, '0'), next: Math.max(...episodes.map(e => e.index)) + 1 })}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="file-input-wrapper">
                <label className="file-input-label" style={{ fontSize: '0.82rem' }}>
                  <span>{appendFile ? appendFile.name : t('script.selectFile')}</span>
                  <input
                    type="file"
                    className="file-input"
                    accept={SCRIPT_UPLOAD_ACCEPT}
                    onChange={async event => {
                      const file = event.target.files?.[0];
                      if (file) {
                        const validation = await validateScriptUploadFile(file);
                        if (validation.ok) {
                          onAppendFileChange(file);
                        } else {
                          onInvalidFile(getScriptFileValidationMessageKey(validation.reason));
                        }
                      }
                      event.target.value = '';
                    }}
                    disabled={appending}
                  />
                </label>
              </div>
              <button className="btn btn-primary btn-sm" disabled={!appendFile || appending} onClick={onAppend}>
                {appending ? (
                  <>
                    <span className="loading-spinner"></span>
                    {t('script.appendingEllipsis')}
                  </>
                ) : t('script.confirmAppend')}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={onCancelAppend} disabled={appending}>
                {t('cancel', { ns: 'common' })}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0, flex: 1, fontSize: '0.95rem' }}>{t('script.title')}</h3>
        <div ref={scriptMenuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => onScriptMenuOpenChange(previous => !previous)}
            aria-label={t('script.moreActions')}
            aria-haspopup="menu"
            aria-expanded={scriptMenuOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              padding: 0,
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--text-2)',
              fontSize: '1.25rem',
              lineHeight: 1,
            }}
            onMouseEnter={(event) => {
              (event.currentTarget as HTMLButtonElement).style.background = 'var(--surface-2)';
            }}
            onMouseLeave={(event) => {
              (event.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            ...
          </button>
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              minWidth: '180px',
              background: 'var(--surface-1)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              padding: '0.25rem 0',
              zIndex: 10,
              maxHeight: scriptMenuOpen ? '2000px' : '0',
              opacity: scriptMenuOpen ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-height 0.25s ease, opacity 0.25s ease',
            }}
          >
            <ImportCodeButton
              projectId={project.id}
              hasScript={!!project.script}
              menuMode
              onActionStart={() => onScriptMenuOpenChange(false)}
              onImported={onImported}
            />
          </div>
        </div>
      </div>
      <form onSubmit={onUpload}>
        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onOpenFormatGuide}
            disabled={uploading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '140px', justifyContent: 'center' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            {t('script.selectFile')}
          </button>
          {selectedFile && (
            <p style={{ marginTop: '0.25rem', color: 'var(--primary)', fontSize: '0.85rem' }}>
              {t('script.fileSelected', { name: selectedFile.name })}
            </p>
          )}
        </div>
        {!project.baseConfigCompleted && (
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: 'var(--danger)' }}>
            {t('script.completeStyleConfigFirst')}
          </p>
        )}
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          style={{ minWidth: '140px', justifyContent: 'center' }}
          disabled={!selectedFile || uploading || !project.baseConfigCompleted}
        >
          {uploading ? (
            <>
              <span className="loading-spinner"></span>
              {t('uploading', { ns: 'common' })}
            </>
          ) : (
            t('script.uploadScriptBtn')
          )}
        </button>
      </form>
    </>
  );
}
